import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Search,
  ShieldCheck,
  FileBadge2,
  HeartHandshake,
  ArrowRight,
  Sparkles,
  BadgeCheck,
  Quote,
  ChevronRight,
} from "lucide-react";
import { SiteShell } from "@/components/site-shell";
import { CauseCard, InstitutionCard } from "@/components/cards";
import { causes, institutions, impactStats, stories, IMG, manavtaLogo } from "@/data/sample";

const logo = { url: manavtaLogo };

export const Route = createFileRoute("/initiatives/animal-seva")({
  head: () => ({
    meta: [
      { title: "Animal Seva — Donate to verified Panjrapols, Gaushalas & shelters" },
      {
        name: "description",
        content:
          "Discover verified animal welfare institutions, support real causes and track exactly how your donation helps animals in need.",
      },
      {
        property: "og:title",
        content: "Animal Seva — Donate to verified animal welfare institutions",
      },
      {
        property: "og:description",
        content:
          "Support Panjrapols, Gaushalas, bird hospitals, rescue centres and animal shelters across India with full transparency.",
      },
    ],
  }),
  component: Home,
});

function Home() {
  const featured = causes.filter((c) => c.featured);
  return (
    <SiteShell>
      <Hero />
      <TrustStrip />
      <FeaturedCauses causes={featured} />
      <HowItWorks />
      <FeaturedInstitutions />
      <Categories />
      <Impact />
      <StoriesPreview />
      <Testimonials />
      <Compliance />
      <FinalCTA />
    </SiteShell>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-cream)" }} />
      <div
        className="absolute -top-32 right-0 -z-10 h-[480px] w-[480px] rounded-full opacity-30 blur-3xl"
        style={{ background: "var(--gradient-warm)" }}
      />
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-7 flex flex-col justify-center">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[var(--maroon)]/15 bg-white/80 py-1 pl-1 pr-3 text-xs font-medium text-[var(--maroon-deep)] shadow-[var(--shadow-soft)]">
            <img src={logo.url} alt="" className="h-6 w-auto rounded-full" />
            An initiative by <strong>Manavta</strong>
            <Sparkles className="h-3.5 w-3.5 text-[var(--saffron-deep)]" />
          </span>
          <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] text-foreground sm:text-5xl lg:text-6xl">
            Support <span className="text-[var(--saffron-deep)]">Panjrapols</span> & animal welfare
            institutions <span className="text-[var(--maroon)]">across India</span>
          </h1>
          <p className="mt-5 max-w-xl text-base text-foreground/70 sm:text-lg">
            Discover verified animal welfare organisations, support real causes and track exactly
            how your donation feeds, heals and shelters animals in need.
          </p>

          <form
            onSubmit={(e) => e.preventDefault()}
            className="mt-7 flex max-w-xl items-center gap-2 rounded-full border border-border bg-white p-1.5 shadow-[var(--shadow-soft)]"
          >
            <Search className="ml-3 h-4 w-4 text-foreground/70" />
            <input
              placeholder="Search Panjrapol, city or cause…"
              className="flex-1 bg-transparent px-2 py-2 text-sm outline-none placeholder:text-foreground/65"
            />
            <Link
              to="/institutions"
              className="rounded-full bg-[var(--maroon)] px-4 py-2 text-sm font-semibold text-[var(--cream)] hover:bg-[var(--maroon-deep)]"
            >
              Search
            </Link>
          </form>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/donate"
              className="rounded-full bg-[var(--maroon)] px-6 py-3 text-sm font-semibold text-[var(--cream)] shadow-[var(--shadow-soft)] hover:bg-[var(--maroon-deep)] transition"
            >
              Donate Now
            </Link>
            <Link
              to="/register-institution"
              className="rounded-full border border-[var(--maroon)]/25 bg-white px-6 py-3 text-sm font-semibold text-[var(--maroon)] hover:bg-[var(--cream-2)] transition"
            >
              Register Your Institution
            </Link>
            <Link
              to="/causes"
              className="inline-flex items-center gap-1 rounded-full px-3 py-3 text-sm font-medium text-foreground/70 hover:text-[var(--maroon)]"
            >
              Explore causes <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[420px] sm:h-[520px]">
            <div className="col-span-4 row-span-4 overflow-hidden rounded-3xl shadow-[var(--shadow-card)]">
              <img src={IMG.heroCow} alt="" className="h-full w-full object-cover" />
            </div>
            <div className="col-span-2 row-span-3 overflow-hidden rounded-3xl shadow-[var(--shadow-card)]">
              <img src={IMG.heroBird} alt="" className="h-full w-full object-cover" />
            </div>
            <div className="col-span-2 row-span-3 overflow-hidden rounded-3xl shadow-[var(--shadow-card)]">
              <img src={IMG.heroDog} alt="" className="h-full w-full object-cover" />
            </div>
            <div className="col-span-4 row-span-2 overflow-hidden rounded-3xl bg-[var(--maroon)] p-5 text-[var(--cream)] flex flex-col justify-center shadow-[var(--shadow-card)]">
              <p className="text-xs uppercase tracking-wider text-[var(--saffron)]">This month</p>
              <p className="mt-1 font-display text-2xl font-semibold">
                ₹42,18,500 disbursed to 87 institutions
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const items = [
    {
      icon: ShieldCheck,
      title: "Admin-verified institutions",
      body: "Every Panjrapol, shelter and hospital is reviewed before going live.",
    },
    {
      icon: FileBadge2,
      title: "Instant 80G receipts",
      body: "Where the institution holds a valid 80G, tax receipts arrive in your inbox.",
    },
    {
      icon: HeartHandshake,
      title: "Choose the cause you trust",
      body: "Donate to fodder, surgery, rescue, ambulance or monthly care — your call.",
    },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
      <div className="grid gap-5 md:grid-cols-3">
        {items.map((i) => (
          <div
            key={i.title}
            className="rounded-2xl border border-border/60 bg-card p-6 shadow-[var(--shadow-soft)]"
          >
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--cream-2)] text-[var(--maroon)]">
              <i.icon className="h-5 w-5" />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold">{i.title}</h3>
            <p className="mt-1.5 text-sm text-foreground/70">{i.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHead({
  eyebrow,
  title,
  link,
  linkText,
}: {
  eyebrow: string;
  title: string;
  link?: string;
  linkText?: string;
}) {
  return (
    <div className="mb-8 flex items-end justify-between gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
          {eyebrow}
        </p>
        <h2 className="mt-2 font-display text-3xl font-semibold sm:text-4xl text-[var(--maroon-deep)]">
          {title}
        </h2>
      </div>
      {link && (
        <Link
          to={link}
          className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[var(--maroon)] hover:underline"
        >
          {linkText} <ChevronRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

function FeaturedCauses({ causes }: { causes: typeof import("@/data/sample").causes }) {
  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
      <SectionHead
        eyebrow="Featured causes"
        title="Causes that need you this week"
        link="/causes"
        linkText="View all causes"
      />
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {causes.map((c) => (
          <CauseCard key={c.id} cause={c} />
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: "01",
      title: "Discover",
      body: "Search verified Panjrapols, shelters and bird hospitals by city, animal type or urgency.",
    },
    {
      n: "02",
      title: "Donate",
      body: "Pick a specific cause, choose an amount and pay securely. Donate as guest or login.",
    },
    {
      n: "03",
      title: "Track impact",
      body: "Receive updates with photos, expense proof and tax receipts directly from the institution.",
    },
  ];
  return (
    <section className="bg-[var(--cream-2)] py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead
          eyebrow="How donation works"
          title="Transparent from first rupee to last update"
        />
        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="rounded-2xl bg-white p-7 shadow-[var(--shadow-soft)]">
              <p className="font-display text-3xl font-semibold text-[var(--saffron-deep)]">
                {s.n}
              </p>
              <h3 className="mt-3 font-display text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-foreground/70">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedInstitutions() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
      <SectionHead
        eyebrow="Trusted institutions"
        title="Verified Panjrapols & animal shelters"
        link="/institutions"
        linkText="See all institutions"
      />
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {institutions.slice(0, 3).map((i) => (
          <InstitutionCard key={i.id} inst={i} />
        ))}
      </div>
    </section>
  );
}

function Categories() {
  const cats = [
    { title: "Cow & Calf Care", body: "Fodder, water, shelter & medical", color: "var(--maroon)" },
    { title: "Stray Dog Rescue", body: "ABC, vaccination & feeding", color: "var(--saffron-deep)" },
    { title: "Bird Hospitals", body: "Kite-season treatment & release", color: "#4a7a6a" },
    { title: "Emergency Surgery", body: "Critical, time-bound causes", color: "#a35b3b" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <SectionHead eyebrow="Welfare categories" title="Donate to the cause you believe in" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cats.map((c) => (
          <Link
            key={c.title}
            to="/causes"
            className="group rounded-2xl border border-border/60 bg-card p-6 shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)]"
          >
            <div className="h-1.5 w-10 rounded-full" style={{ background: c.color }} />
            <h3 className="mt-4 font-display text-lg font-semibold">{c.title}</h3>
            <p className="mt-1 text-sm text-foreground/65">{c.body}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--maroon)] group-hover:gap-2 transition-all">
              Explore causes <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Impact() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
      <div className="rounded-3xl bg-[var(--maroon)] p-10 text-[var(--cream)] shadow-[var(--shadow-card)]">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron)]">
          Combined impact
        </p>
        <h2 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">
          Every rupee, accounted for
        </h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {impactStats.map((s) => (
            <div key={s.label}>
              <p className="font-display text-4xl font-semibold text-[var(--saffron)]">{s.value}</p>
              <p className="mt-1 text-sm text-[var(--cream)]/75">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StoriesPreview() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
      <SectionHead
        eyebrow="Real stories"
        title="Lives changed by your donations"
        link="/stories"
        linkText="Read all stories"
      />
      <div className="grid gap-6 md:grid-cols-3">
        {stories.map((s) => (
          <Link
            key={s.id}
            to="/stories/$id"
            params={{ id: s.id }}
            className="group overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)]"
          >
            <img
              src={s.image}
              alt=""
              loading="lazy"
              className="aspect-[16/10] w-full object-cover"
            />
            <div className="p-5">
              <p className="text-xs text-foreground/70">
                {new Date(s.date).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </p>
              <h3 className="mt-1.5 font-display text-lg font-semibold leading-snug">{s.title}</h3>
              <p className="mt-2 text-sm text-foreground/70 line-clamp-2">{s.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Testimonials() {
  const t = [
    {
      quote:
        "I sponsor monthly fodder for 5 cows. The institution sends me photos and feed bills every month.",
      who: "Rahul S., Mumbai",
    },
    {
      quote:
        "Donating to surgery causes here feels personal — I see the calf's recovery photos two weeks later.",
      who: "Priya M., Bengaluru",
    },
    {
      quote:
        "Got my 80G receipt within 90 seconds. First donation platform that actually delivers.",
      who: "Amit J., Surat",
    },
  ];
  return (
    <section className="bg-[var(--cream-2)] py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead eyebrow="Donor voices" title="Why donors keep coming back" />
        <div className="grid gap-5 md:grid-cols-3">
          {t.map((x) => (
            <div key={x.who} className="rounded-2xl bg-white p-6 shadow-[var(--shadow-soft)]">
              <Quote className="h-5 w-5 text-[var(--saffron-deep)]" />
              <p className="mt-3 text-sm leading-relaxed text-foreground/80">"{x.quote}"</p>
              <p className="mt-4 text-xs font-medium text-foreground/70">— {x.who}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Compliance() {
  const items = [
    "Institution-level verification (registration, PAN, bank, address)",
    "Optional 80G & 12A document checks",
    "Cause approval before public listing",
    "Mandatory expense proof on every impact update",
    "Full donation trail downloadable as PDF",
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
      <div className="grid items-center gap-10 rounded-3xl border border-border/60 bg-card p-8 shadow-[var(--shadow-soft)] md:grid-cols-2 md:p-12">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
            Compliance & trust
          </p>
          <h2 className="mt-2 font-display text-3xl font-semibold text-[var(--maroon-deep)]">
            Five safeguards behind every donation
          </h2>
          <p className="mt-3 text-foreground/70">
            Animal Seva is built around transparency. Institutions only appear publicly after admin
            verification, and donors can track exactly how every cause's funds were used.
          </p>
        </div>
        <ul className="space-y-3">
          {items.map((i) => (
            <li key={i} className="flex items-start gap-3 rounded-xl bg-[var(--cream-2)] p-4">
              <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-[var(--success)]" />
              <span className="text-sm text-foreground/80">{i}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
      <div
        className="overflow-hidden rounded-3xl p-10 text-center text-[var(--cream)] sm:p-14"
        style={{ background: "var(--gradient-warm)" }}
      >
        <h2 className="font-display text-3xl font-semibold sm:text-4xl">
          Your gift becomes someone's new beginning
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-[var(--cream)]/90">
          Every donation feeds, heals or shelters an animal somewhere in India today.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link
            to="/donate"
            className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-[var(--maroon)] hover:bg-[var(--cream-2)] transition"
          >
            Donate Now
          </Link>
          <Link
            to="/register-institution"
            className="rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition"
          >
            Register Your Institution
          </Link>
        </div>
      </div>
    </section>
  );
}
